import {
  VPPEquipmentEvents,
  VPPEventStatus,
  VPPProgramOffer,
  VPPUtility,
} from 'clipsal-cortex-types/src/api/api-saturn-vpp';

import { baseApi } from '../../../app/services/baseApi';

export type CreateCustomerAgreement = {
  utility_account_number: string;
  utility_mrid: string;
};

export type UpdateVPPEvent = {
  event_status: VPPEventStatus;
};

export type RespondToProgramOffer = {
  program_mrid: string;
  battery_mrid: string;
  is_accepted?: boolean;
};

export const vppApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Returns the utility providers that the user can select from for VPP enrolment
    getVppUtilities: build.query<VPPUtility[], number>({
      query: (inverterId) => `/v1/site_devices/${inverterId}/vpp_utilities`,
      providesTags: ['VPPUtilities'],
    }),
    // Saves the utility details to be applicable for VPP enrolment
    createVppCustomerAgreement: build.mutation<void, { inverterId: number; body: CreateCustomerAgreement }>({
      query: ({ inverterId, body }) => {
        return {
          url: `/v1/site_devices/${inverterId}/vpp_customer_agreements`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['VPPProgramOffers'],
    }),
    // Returns all VPP program offer notifications for the Saturn inverter
    getVppProgramOffers: build.query<VPPProgramOffer[], number>({
      query: (inverterId) => `/v1/site_devices/${inverterId}/vpp_program_offers`,
      providesTags: ['VPPProgramOffers'],
    }),
    respondToVppProgramOffer: build.mutation<void, { inverterId: number; body: RespondToProgramOffer }>({
      query: ({ inverterId, body }) => {
        return {
          url: `/v1/site_devices/${inverterId}/vpp_program_offers`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['VPPProgramOffers', 'VPPEvents'],
    }),
    // Returns all current/scheduled VPP events for each 'equipment' part of the Saturn inverter
    getVppEvents: build.query<VPPEquipmentEvents[], number>({
      query: (inverterId) => `/v1/site_devices/${inverterId}/vpp_events`,
      providesTags: ['VPPEvents'],
    }),
    updateVPPEvent: build.mutation<void, { inverterId: number; vppEventId: string; body: UpdateVPPEvent }>({
      query: ({ inverterId, vppEventId, body }) => {
        return {
          url: `/v1/site_devices/${inverterId}/vpp_events/${vppEventId}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['VPPEvents'],
    }),
  }),
});

export const {
  useGetVppProgramOffersQuery,
  useGetVppUtilitiesQuery,
  useGetVppEventsQuery,
  useCreateVppCustomerAgreementMutation,
  useRespondToVppProgramOfferMutation,
  useUpdateVPPEventMutation,
} = vppApi;

export function useGetVppProgramOffers(inverterId: number, skip = false) {
  const { data: programOffers, ...rest } = useGetVppProgramOffersQuery(inverterId, { skip });
  return { programOffers: programOffers ?? [], ...rest };
}

export function useGetVppUtilities(inverterId: number) {
  const { data: utilities, ...rest } = useGetVppUtilitiesQuery(inverterId);
  return { utilities: utilities ?? [], ...rest };
}

export function useGetVppEvents(inverterId: number) {
  const { data, ...rest } = useGetVppEventsQuery(inverterId);
  return { data: data ?? [], ...rest };
}
